import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Building2,
  Truck,
  UserRound,
  Clock3,
  Loader2,
  Package,
  ArrowUpRight,
  Store
} from 'lucide-react';
import { useAuth } from '@/shared/contexts';
import {
  superAdminApi,
  type SuperAdminAnalyticsOverview
} from '@/pages/SuperAdmin/api/superAdminApi';
import { Alert } from '@/shared/components/ui/Alert';
import { SaDataTable, type SaColumn } from '@/pages/SuperAdmin/SaDataTable';
import { Box } from '@mui/material';

const StatCard: React.FC<{
  label: string;
  value: number | string;
  icon: React.ReactNode;
  hint?: string;
}> = ({ label, value, icon, hint }) => (
  <div className="relative overflow-hidden rounded-2xl border border-border-primary bg-background-card p-5 shadow-sm">
    <div className="flex items-start justify-between gap-3">
      <div>
        <p className="text-sm font-medium text-text-tertiary">{label}</p>
        <p className="mt-2 text-3xl font-semibold tracking-tight text-text-primary">
          {value}
        </p>
        {hint && <p className="mt-1 text-xs text-text-muted">{hint}</p>}
      </div>
      <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#006B82]/10 text-[#006B82]">
        {icon}
      </div>
    </div>
  </div>
);

type RankRow = { id: string; name: string; count: number; rank: number };

const rankColumns: SaColumn<RankRow>[] = [
  {
    id: 'rank',
    label: '#',
    width: '12%',
    render: row => (
      <Box sx={{ color: 'var(--color-text-muted)' }}>{row.rank}</Box>
    )
  },
  {
    id: 'name',
    label: 'Name',
    width: '68%',
    render: row => (
      <Box sx={{ fontWeight: 600, color: 'var(--color-text-primary)' }}>
        {row.name}
      </Box>
    )
  },
  {
    id: 'orders',
    label: 'Orders',
    align: 'right',
    width: '20%',
    render: row => (
      <Box sx={{ fontWeight: 700, color: '#006B82' }}>{row.count}</Box>
    )
  }
];

const RankTable: React.FC<{
  title: string;
  empty: string;
  rows: { id: string; name: string; count: number }[];
}> = ({ title, empty, rows }) => (
  <div className="space-y-0">
    <div className="mb-0 flex items-center justify-between rounded-t-2xl border border-b-0 border-border-primary bg-background-card px-5 py-4">
      <h3 className="font-semibold text-text-primary">{title}</h3>
      <span className="text-xs text-text-muted">Last 30 days</span>
    </div>
    <SaDataTable
      columns={rankColumns}
      rows={rows.map((row, i) => ({ ...row, rank: i + 1 }))}
      getRowId={row => row.id}
      emptyMessage={empty}
      minWidth={420}
    />
  </div>
);

const statusColumns: SaColumn<{ id: string; status: string; count: number }>[] =
  [
    {
      id: 'status',
      label: 'Status',
      width: '70%',
      render: row => (
        <Box sx={{ textTransform: 'capitalize' }}>
          {row.status.replace(/_/g, ' ')}
        </Box>
      )
    },
    {
      id: 'count',
      label: 'Count',
      align: 'right',
      width: '30%',
      render: row => <Box sx={{ fontWeight: 600 }}>{row.count}</Box>
    }
  ];

export const SuperAdminDashboardPage: React.FC = () => {
  const { accessToken } = useAuth();
  const [data, setData] = useState<SuperAdminAnalyticsOverview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!accessToken) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await superAdminApi.getAnalytics(accessToken);
        if (!cancelled) setData(res.data);
      } catch (err: unknown) {
        if (!cancelled) {
          const message =
            (err as { response?: { data?: { message?: string } } })?.response
              ?.data?.message || 'Failed to load analytics';
          setError(Array.isArray(message) ? message.join(', ') : message);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [accessToken]);

  if (loading) {
    return (
      <div className="flex justify-center py-24">
        <Loader2 className="h-8 w-8 animate-spin text-[#006B82]" />
      </div>
    );
  }

  const statusRows7 = Object.entries(data?.ordersLast7Days.byStatus ?? {});
  const statusRows30 = Object.entries(data?.ordersLast30Days.byStatus ?? {});

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight text-text-primary">
            Dashboard
          </h2>
          <p className="mt-1 text-sm text-text-tertiary">
            Restaurants, suppliers, and order activity at a glance
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link
            to="/super-admin/restaurants"
            className="inline-flex items-center gap-1.5 rounded-lg border border-border-primary bg-background-card px-3 py-2 text-sm font-medium text-text-secondary hover:bg-background-hover"
          >
            Restaurants <ArrowUpRight className="h-4 w-4" />
          </Link>
          <Link
            to="/super-admin/suppliers"
            className="inline-flex items-center gap-1.5 rounded-lg border border-border-primary bg-background-card px-3 py-2 text-sm font-medium text-text-secondary hover:bg-background-hover"
          >
            Suppliers <ArrowUpRight className="h-4 w-4" />
          </Link>
          <Link
            to="/super-admin/orders"
            className="inline-flex items-center gap-1.5 rounded-lg bg-[#006B82] px-3 py-2 text-sm font-medium text-white hover:bg-[#00809C]"
          >
            Place orders <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>
      </div>

      {error && <Alert variant="destructive">{error}</Alert>}

      {data && (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <StatCard
              label="Restaurants"
              value={data.totals.restaurants}
              hint="All statuses"
              icon={<Store className="h-5 w-5" />}
            />
            <StatCard
              label="Suppliers"
              value={data.totals.suppliers}
              hint="All statuses"
              icon={<Truck className="h-5 w-5" />}
            />
            <StatCard
              label="Employees"
              value={data.totals.employees}
              hint="All statuses"
              icon={<UserRound className="h-5 w-5" />}
            />
            <StatCard
              label="Pending access"
              value={data.pendingAccessRequests}
              hint="Needs review"
              icon={<Clock3 className="h-5 w-5" />}
            />
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <div className="rounded-2xl border border-border-primary bg-background-card p-5 shadow-sm">
              <div className="mb-4 flex items-center gap-2">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#006B82]/10 text-[#006B82]">
                  <Package className="h-4 w-4" />
                </div>
                <div>
                  <h3 className="font-semibold text-text-primary">
                    Orders · 7 days
                  </h3>
                  <p className="text-xs text-text-muted">By status</p>
                </div>
                <p className="ml-auto text-2xl font-semibold text-[#006B82]">
                  {data.ordersLast7Days.total}
                </p>
              </div>
              <SaDataTable
                columns={statusColumns}
                rows={statusRows7.map(([status, count]) => ({
                  id: status,
                  status,
                  count
                }))}
                getRowId={row => row.id}
                emptyMessage="No orders in this period"
                minWidth={280}
              />
            </div>

            <div className="rounded-2xl border border-border-primary bg-background-card p-5 shadow-sm">
              <div className="mb-4 flex items-center gap-2">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#006B82]/10 text-[#006B82]">
                  <Building2 className="h-4 w-4" />
                </div>
                <div>
                  <h3 className="font-semibold text-text-primary">
                    Orders · 30 days
                  </h3>
                  <p className="text-xs text-text-muted">By status</p>
                </div>
                <p className="ml-auto text-2xl font-semibold text-[#006B82]">
                  {data.ordersLast30Days.total}
                </p>
              </div>
              <SaDataTable
                columns={statusColumns}
                rows={statusRows30.map(([status, count]) => ({
                  id: status,
                  status,
                  count
                }))}
                getRowId={row => row.id}
                emptyMessage="No orders in this period"
                minWidth={280}
              />
            </div>
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <RankTable
              title="Top suppliers"
              empty="No supplier orders yet"
              rows={data.topSuppliers.map(s => ({
                id: s.supplierId,
                name: s.businessName || s.supplierId,
                count: s.orderCount
              }))}
            />
            <RankTable
              title="Top restaurants"
              empty="No restaurant orders yet"
              rows={data.topRestaurants.map(r => ({
                id: r.restaurantId,
                name: r.businessName || r.restaurantId,
                count: r.orderCount
              }))}
            />
          </div>
        </>
      )}
    </div>
  );
};
